import { useCallback, useState } from 'react'

import useProbateEstateCollection from 'lib/probate/probate-estate-collection'
import {
  ProbateEstateItem,
  ProbateEstateItemType,
} from 'lib/models/probate-estate'

import { RecordCollection } from './records-section'

// items saved before the questionnaire may have no ownership, and shares
// come back from the database as decimal strings like "50.00"
const withDefaults = (item: ProbateEstateItem): ProbateEstateItem => {
  const { ownership, deceasedShareOfJointOwnership: share } = item.attributes
  return {
    ...item,
    attributes: {
      ...item.attributes,
      ownership: ownership || 'sole',
      ...(share !== null && share !== undefined
        ? { deceasedShareOfJointOwnership: Number(share) }
        : {}),
    },
  }
}

// every estate item type shares one /items endpoint, so this fetches
// them once and hands each sub-section a view of just its own type
const useEstateItems = ({ probateEstateId }: { probateEstateId: number }) => {
  const { items, createItem, updateItem, deleteItem, isCreating } =
    useProbateEstateCollection({
      probateEstateId,
      collection: 'items',
      type: 'probate_estate_items',
    })

  const [creatingItemType, setCreatingItemType] =
    useState<ProbateEstateItemType>()

  const forType = useCallback(
    (itemType: ProbateEstateItemType): RecordCollection => ({
      items: (items as ProbateEstateItem[])
        .filter((item) => item.attributes.itemType === itemType)
        .map(withDefaults),
      createItem: async (attributes = {}) => {
        setCreatingItemType(itemType)
        try {
          return await createItem({
            itemType,
            ownership: 'sole',
            ...attributes,
          })
        } finally {
          setCreatingItemType(undefined)
        }
      },
      // the server requires itemType whenever ownership is sent
      updateItem: (id, attributes) =>
        updateItem(id, { ...attributes, itemType }),
      deleteItem,
      isCreating: isCreating && creatingItemType === itemType,
    }),
    [items, createItem, updateItem, deleteItem, isCreating, creatingItemType]
  )

  return { forType }
}

export default useEstateItems
